import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "./axios"; // Your configured Axios instance
import { Category } from "../types/services.types";


interface CreateCategoryRequest {
  name: string;
  service_ids?: number[];
}

interface UpdateCategoryRequest {
  name?: string;
  service_ids?: number[];
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  errors?: Record<string, string[]>;
}

// React Query Keys
export const categoryKeys = {
  all: ['categories'] as const,
  sign: () => [...categoryKeys.all, 'sign'] as const,
  signList: () => [...categoryKeys.sign(), 'list'] as const,
  signDetail: (id: number) => [...categoryKeys.sign(), 'detail', id] as const,
};

// Fetch all sign categories
export const useGetAllCategories = (withServices = false) => {
  const { data, error, ...res } = useQuery({
    queryKey: [...categoryKeys.signList(), { withServices }],
    queryFn: async (): Promise<Category[]> => {
      const response = await axios.get<ApiResponse<Category[]>>('/sign-categories', {
        params: withServices ? { with_services: true } : {}
      });
      return response.data.data;
    },
  });

  return { categories: data ?? [], error, ...res };
};

// Fetch single sign category
export const useGetCategory = (id: number, withServices = false) => {
  const { data, error, ...res } = useQuery({
    queryKey: [...categoryKeys.signDetail(id), { withServices }],
    queryFn: async (): Promise<Category> => {
      const response = await axios.get<ApiResponse<Category>>(`/sign-categories/${id}`, {
        params: withServices ? { with_services: true } : {}
      });
      return response.data.data;
    },
    enabled: !!id, // Only run if id is provided
  });

  return { category: data, error, ...res };
};

// Create sign category
export const useCreateCategory = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: CreateCategoryRequest): Promise<Category> => {
      const response = await axios.post<ApiResponse<Category>>('/sign-categories', data);
      return response.data.data;
    },
    onSuccess: () => {
      // Invalidate and refetch categories list
      queryClient.invalidateQueries({ queryKey: categoryKeys.signList() });
    },
    onError: (error: any) => {
      console.error('Error creating category:', error);
    }
  });
};

// Update sign category
export const useUpdateCategory = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: UpdateCategoryRequest }): Promise<Category> => {
      const response = await axios.put<ApiResponse<Category>>(`/sign-categories/${id}`, data);
      return response.data.data;
    },
    onSuccess: (variables) => {
      // Invalidate and refetch categories list
      queryClient.invalidateQueries({ queryKey: categoryKeys.signList() });
      // Update the specific category in cache
      queryClient.invalidateQueries({ queryKey: categoryKeys.signDetail(variables.id) });
    },
    onError: (error: any) => {
      console.error('Error updating category:', error);
    }
  });
};

// Delete sign category
export const useDeleteCategory = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await axios.delete(`/sign-categories/${id}`);
    },
    onSuccess: () => {
      // Invalidate and refetch categories list
      queryClient.invalidateQueries({ queryKey: categoryKeys.signList() });
    },
    onError: (error: any) => {
      console.error('Error deleting category:', error);
    }
  });
};

